import axios, { AxiosError } from "axios";
import { usePersistentStore } from "@/stores/persistent";
import { useApi } from "@/composables/useApi";
import { customizedErrorResponseHandler, handleAxiosError, isCustomizedErrorResponse } from "@/functions/handler";
import { CustomizedResponse, RequestOption } from "@/types/functions";
import { VersionRequirements } from "@/types/others";

const { api } = useApi()

const request = async <T = unknown>(reqParams: RequestOption, showError = false, offset?: number): Promise<T | undefined> => {
    const { method, route, body, query, responseType } = reqParams

    const response = await api.request({
        method: method,
        url: route,
        data: body,
        params: query,
        responseType: responseType
    })
        .catch(handleAxiosError)

    if (isCustomizedErrorResponse(response)) {
        if (showError) await customizedErrorResponseHandler(response, undefined, offset)
        return undefined
    }

    return response.data
}

const requestBool = async (reqParams: RequestOption, showError = false, offset?: number): Promise<boolean> => {
    const { method, route, body, query, responseType } = reqParams

    const response = await api.request({
        method: method,
        url: route,
        data: body,
        params: query,
        responseType: responseType
    })
        .catch(handleAxiosError)

    if (isCustomizedErrorResponse(response)) {
        if (showError) await customizedErrorResponseHandler(response, undefined, offset)
        return false
    }

    return Boolean(response);
}

const requestRaw = async (reqParams: RequestOption): Promise<CustomizedResponse> => {
    const { method, route, body, query, responseType } = reqParams

    return await api.request({
        method: method,
        url: route,
        data: body,
        params: query,
        responseType: responseType
    })
        .catch(handleAxiosError)
}

const pingRequest = async (): Promise<boolean> => {
    const response = await axios.get(usePersistentStore().apiUrl + 'ping', { timeout: 5000 }).catch(() => {
        return false
    })

    if (response) return Boolean(response)

    return new Promise((resolve) => {
        setTimeout(async () => {
            const secondTry = Boolean(await axios.get(usePersistentStore().apiUrl + 'ping', { timeout: 5000 }).catch(() => {
                return false
            }))
            resolve(secondTry)
        }, 3000)
    })

}

export const pingVersions = async (): Promise<VersionRequirements | undefined> => {
    const response = await axios.get(`${usePersistentStore().apiUrl}ping`, { timeout: 5000, headers: { Accept: '' } })
        .catch((err: AxiosError) => {
            console.error(err.response?.data)
            return undefined
        })

    if (!response?.headers) return undefined

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    //@ts-ignore
    return JSON.parse(response.headers.get('X-App-Min-Versions'))
}

export {
    request,
    requestBool,
    requestRaw,
    pingRequest
}
